import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Timer from '../components/Timer';
import ManualEntry from '../components/ManualEntry';
import BadgeDisplay from '../components/BadgeDisplay';
import SessionHistory from '../components/SessionHistory';
import Stats from '../components/Stats';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/sessions`)
      ]);
      setStats(statsRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSessionCreated = () => {
    fetchData();
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      toast.success('Session deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-emerald-600 text-xl font-medium">Loading your outdoor journey...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-2xl">⏱️</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-emerald-900">FreshAir Timer</h1>
                <p className="text-sm text-emerald-600">Welcome back, {user.name}!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user.is_admin && (
                <Button
                  onClick={() => window.location.href = '/admin'}
                  data-testid="admin-btn"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  Admin Panel
                </Button>
              )}
              <Button
                onClick={onLogout}
                data-testid="logout-btn"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <Stats stats={stats} />

        {/* Badges */}
        <BadgeDisplay badges={stats?.badges || []} />

        {/* Timer and History */}
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <div>
            <Tabs defaultValue="timer" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="timer" data-testid="timer-tab">Timer</TabsTrigger>
                <TabsTrigger value="manual" data-testid="manual-entry-tab">Manual Entry</TabsTrigger>
              </TabsList>
              <TabsContent value="timer">
                <Timer onSessionCreated={handleSessionCreated} />
              </TabsContent>
              <TabsContent value="manual">
                <ManualEntry onSessionCreated={handleSessionCreated} />
              </TabsContent>
            </Tabs>
          </div>

          <div>
            <SessionHistory
              sessions={sessions}
              onDeleteSession={handleDeleteSession}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;